import { pollPosition } from "./api";
import { createTrackerMap } from "./map";
import { formatEta, formatRelative, shortenTechName } from "./format";
import type { AppConfig, PositionResponse, VisitStatus } from "./types";

declare global {
  interface Window {
    __TECH_TRACKER_CONFIG__?: Partial<AppConfig>;
  }
}

const DEFAULT_CONFIG: AppConfig = {
  positionEndpoint: "https://jcataldo.app.n8n.cloud/webhook/tech-position",
  mapboxToken: "", // injected at build time via window.__TECH_TRACKER_CONFIG__
  pollIntervalMs: 30_000,
  arrivedPollIntervalMs: 60_000,
};

const config: AppConfig = { ...DEFAULT_CONFIG, ...(window.__TECH_TRACKER_CONFIG__ ?? {}) };

const banner = document.getElementById("status-banner") as HTMLElement;
const techNameEl = document.getElementById("tech-name") as HTMLElement;
const statusPill = document.getElementById("status-pill") as HTMLElement;
const etaEl = document.getElementById("eta") as HTMLElement;
const lastUpdatedEl = document.getElementById("last-updated") as HTMLElement;
const mapContainer = document.getElementById("map") as HTMLElement;

const params = new URLSearchParams(location.search);
const ref = params.get("ref");

if (!ref) {
  setTerminal("expired", "Tracking link missing", "Ask Goldin Solar to resend the link.");
} else if (!config.mapboxToken) {
  setTerminal("error", "Map unavailable", "Map credentials are not configured.");
} else {
  startTracking(ref);
}

function statusLabel(status: VisitStatus): string {
  switch (status) {
    case "en_route":
      return "On the way";
    case "arrived":
      return "On site";
    case "complete":
      return "Visit complete";
  }
}

function setTerminal(state: "expired" | "error" | "complete", title: string, sub: string) {
  banner.dataset.state = state;
  techNameEl.textContent = title;
  statusPill.textContent = state === "complete" ? "Complete" : "Done";
  etaEl.textContent = sub;
  lastUpdatedEl.textContent = "";
}

function startTracking(refToken: string) {
  const trackerMap = createTrackerMap(mapContainer, config.mapboxToken);
  let timer: number | undefined;
  let lastAsOf: string | null = null;
  let stopped = false;

  async function tick() {
    if (stopped) return;
    let result: PositionResponse;
    try {
      result = await pollPosition(config.positionEndpoint, refToken);
    } catch (err) {
      console.warn("[tracker] poll failed", err);
      schedule(config.pollIntervalMs);
      return;
    }

    if (!result.ok) {
      handleError(result.reason);
      return;
    }

    lastAsOf = result.tech.asOf;
    banner.dataset.state = result.status;
    techNameEl.textContent = `${shortenTechName(result.tech.name)} from Goldin Solar`;
    statusPill.textContent = statusLabel(result.status);
    etaEl.textContent =
      result.status === "complete" ? "Thanks for choosing Goldin Solar" : formatEta(result.etaMinutes);
    lastUpdatedEl.textContent = `Updated ${formatRelative(result.lastUpdatedAt)}`;

    trackerMap.update(result);

    if (result.status === "complete") {
      stopped = true;
      return;
    }

    const interval =
      result.status === "arrived" ? config.arrivedPollIntervalMs : config.pollIntervalMs;
    schedule(interval);
  }

  function schedule(ms: number) {
    if (stopped) return;
    timer = window.setTimeout(tick, ms);
  }

  function handleError(reason: PositionResponse extends { ok: false; reason: infer R } ? R : never) {
    stopped = true;
    if (reason === "complete") {
      setTerminal("complete", "Visit complete", "Thanks for choosing Goldin Solar");
      return;
    }
    if (reason === "expired") {
      setTerminal("expired", "Tracking link expired", "Ask us to resend the link.");
      return;
    }
    if (reason === "invalid_signature" || reason === "not_found") {
      setTerminal("expired", "Tracking link invalid", "Ask us to resend the link.");
      return;
    }
    setTerminal("error", "Connection issue", "Trying again shortly.");
    stopped = false;
    schedule(15_000);
  }

  // Refresh "updated X ago" between polls
  window.setInterval(() => {
    if (lastAsOf) lastUpdatedEl.textContent = `Updated ${formatRelative(lastAsOf)}`;
  }, 5_000);

  // Pause polling when tab hidden, resume when visible
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      if (timer) window.clearTimeout(timer);
    } else if (!stopped) {
      tick();
    }
  });

  tick();
}
